import { createThemeContract } from '@vanilla-extract/css';
import {
  black,
  gray,
  green,
  lightBlue,
  lightOrange,
  lightTeal,
  lightYellow,
  orange,
  purple,
  red,
  white,
} from '../palette';
import { processLightDarkTokens } from './theme-utils';
import { smTokens } from './semantic-vars.css';

/**
 * NOTE:
 * Please use only primitive tokens as values for light-dark variables.
 * Do not use css vars
 * Otherwise ThemeColorsProvider will not have resolved value.
 */

const componentTokens = processLightDarkTokens({
  base: {
    background: [smTokens.surface.base, gray[900]],
  },
  container: {
    border: {
      default: [smTokens.border.quaternary, gray[700]],
      secondary: [smTokens.border.tertiary, gray[600]],
    },
    foreground: {
      primary: [smTokens.text.primary, smTokens.text.inverse],
      secondary: [smTokens.text.secondary, white[600]],
      tertiary: [smTokens.text.tertiary, white[500]],
      disabled: [smTokens.text.disabled, white[400]],
      brand: [purple[500], purple[300]],
      success: [smTokens.text.alert.success, green[700]],
      warning: [smTokens.text.alert.warning, orange[600]],
      critical: [smTokens.text.alert.critical, red[600]],
    },
    primary: {
      background: {
        idle: [smTokens.surface.primary.default, gray[800]],
        hover: [smTokens.surface.primary.hover, gray[700]],
      },
    },
    secondary: {
      background: {
        idle: [smTokens.surface.secondary.default, gray[700]],
        hover: [smTokens.surface.secondary.hover, gray[600]],
      },
    },
    tertiary: {
      background: {
        idle: [smTokens.surface.tertiary.default, gray[900]],
      },
    },
    prominent: {
      background: {
        idle: [smTokens.surface.brand.default, purple[800]],
      },
    },
  },

  nav: {
    color: [white[900], white[900]],
    base: [smTokens.surface.nav, gray[900]],
    border: ['transparent', gray[700]],

    button: {
      background: {
        idle: [smTokens.bg.nav.default, gray[900]],
        hover: [smTokens.bg.nav.hover, smTokens.bg.nav.hover],
        active: [smTokens.bg.nav.active, smTokens.bg.nav.active],
      },
      foreground: {
        idle: [smTokens.text.nav.default, gray[500]],
        selected: [smTokens.text.inverse, smTokens.text.inverse],
      },
      disabled: {
        background: [smTokens.bg.nav.disabled, 'transparent'],
        foreground: [smTokens.text.disabled, smTokens.text.tertiary],
      },
      border: [smTokens.surface.base, gray[700]],
    },

    collapseButton: {
      background: {
        idle: [smTokens.bg.brand.default, gray[900]],
        hover: [smTokens.bg.brand.hover, gray[800]],
        active: [smTokens.bg.brand.active, gray[700]],
      },
      foreground: {
        idle: [smTokens.text.nav.default, gray[500]],
      },
      border: {
        default: [smTokens.border.primary, gray[700]],
        disabled: [smTokens.border.disabled, gray[700]],
      },
    },
  },

  button: {
    primary: {
      foreground: [smTokens.text.inverse, smTokens.text.brand],

      background: {
        idle: [smTokens.bg.brand.default, white[900]],
        hover: [smTokens.bg.brand.hover, smTokens.bg.inverse.hover],
        active: [smTokens.bg.brand.active, smTokens.bg.inverse.active],
      },
    },

    secondary: {
      foreground: [smTokens.text.brand, smTokens.text.inverse],
      background: {
        idle: [smTokens.bg.secondary.default, white[300]],
        hover: [smTokens.bg.secondary.hover, white[400]],
        active: [smTokens.bg.secondary.active, white[500]],
      },
    },

    tertiary: {
      foreground: [smTokens.text.brand, smTokens.text.inverse],
      border: [smTokens.border.brand, smTokens.border.inverse],

      background: {
        hover: [smTokens.bg.tertiary.hover, white[200]],
        active: [smTokens.bg.tertiary.active, white[300]],
      },
    },

    inverted: {
      foreground: [smTokens.text.brand, smTokens.text.inverse],

      background: {
        idle: [smTokens.bg.inverse.default, smTokens.bg.brand.default],
        hover: [smTokens.bg.inverse.hover, smTokens.bg.brand.hover],
        active: [smTokens.bg.inverse.active, smTokens.bg.brand.active],
      },
    },

    disabled: {
      foreground: [smTokens.text.disabled, smTokens.text.disabled],
      background: [smTokens.bg.disabled, white[100]],
    },
  },

  tab: {
    foreground: {
      default: [smTokens.text.secondary, gray[500]],
      hover: [smTokens.text.brand, smTokens.text.inverse],
      active: [smTokens.text.brand, smTokens.text.inverse],
      disabled: [smTokens.text.disabled, white[400]],
    },
    border: {
      active: [smTokens.border.brand, purple[300]],
    },
  },

  tag: {
    foreground: [smTokens.text.brand, smTokens.text.brand],
    borderBrand: [smTokens.border.brand, 'transparent'],
    borderSecondary: [smTokens.border.secondary, smTokens.border.primary],
    borderTertiary: [smTokens.border.tertiary, smTokens.border.secondary],

    default: {
      foreground: [smTokens.text.brand, smTokens.text.inverse],

      background: {
        idle: [
          smTokens.surface.tertiary.default,
          smTokens.surface.tertiary.default,
        ],
        hover: [
          smTokens.surface.tertiary.hover,
          smTokens.surface.tertiary.hover,
        ],
        active: [
          smTokens.surface.tertiary.active,
          smTokens.surface.tertiary.active,
        ],
      },
    },

    brand: {
      background: {
        idle: [smTokens.surface.brand.default, purple[200]],
        hover: [smTokens.surface.brand.hover, purple[300]],
        active: [smTokens.surface.brand.active, purple[400]],
      },
    },

    auxLightBlue: {
      background: {
        idle: [smTokens.surface.aux.lightBlue.default, lightBlue[200]],
        hover: [smTokens.surface.aux.lightBlue.hover, lightBlue[400]],
        active: [smTokens.surface.aux.lightBlue.active, lightBlue[500]],
      },
    },

    auxLightOrange: {
      background: {
        idle: [smTokens.surface.aux.lightOrange.default, lightOrange[200]],
        hover: [smTokens.surface.aux.lightOrange.hover, lightOrange[400]],
        active: [smTokens.surface.aux.lightOrange.active, lightOrange[500]],
      },
    },

    auxLightTeal: {
      background: {
        idle: [smTokens.surface.aux.lightTeal.default, lightTeal[200]],
        hover: [smTokens.surface.aux.lightTeal.hover, lightTeal[300]],
        active: [smTokens.surface.aux.lightTeal.active, lightTeal[400]],
      },
    },

    auxLightYellow: {
      background: {
        idle: [smTokens.surface.aux.lightYellow.default, lightYellow[200]],
        hover: [smTokens.surface.aux.lightYellow.hover, lightYellow[300]],
        active: [smTokens.surface.aux.lightYellow.active, lightYellow[400]],
      },
    },

    selection: {
      foreground: [smTokens.text.brand, smTokens.text.inverse],
      border: [smTokens.border.tertiary, smTokens.border.tertiary],

      background: {
        idle: [smTokens.surface.primary.default, 'transparent'],
        hover: [smTokens.surface.primary.hover, gray[700]],
        active: [smTokens.surface.primary.active, gray[600]],
        selected: [smTokens.surface.brand.default, purple[400]],
      },
    },

    disabled: {
      foreground: [smTokens.text.disabled, smTokens.text.disabled],
      background: [smTokens.surface.disabled, white[400]],
      border: [smTokens.border.disabled, smTokens.border.disabled],
    },
  },

  badge: {
    foreground: {
      default: [smTokens.text.primary, smTokens.text.primary],
      primary: [smTokens.text.primary, smTokens.text.inverse],
      success: [smTokens.text.alert.success, smTokens.text.alert.success],
      critical: [smTokens.text.alert.critical, smTokens.text.alert.critical],
    },

    border: {
      default: [smTokens.border.primary, smTokens.border.primary],

      secondary: [smTokens.border.secondary, smTokens.border.primary],
      tertiary: [smTokens.border.tertiary, smTokens.border.secondary],
      brand: [smTokens.border.primary, 'transparent'],
    },

    primary: {
      background: {
        idle: [smTokens.surface.secondary.default, gray[700]],
        hover: [smTokens.surface.secondary.hover, gray[600]],
        active: [smTokens.surface.secondary.active, gray[600]],
      },
    },

    neutral: {
      background: {
        idle: [gray[300], gray[400]],
        hover: [gray[400], gray[500]],
        active: [gray[500], gray[600]],
      },
    },

    brand: {
      background: {
        idle: [purple[50], purple[200]],
        hover: [purple[100], purple[300]],
        active: [purple[200], purple[400]],
      },
    },

    auxLightBlue: {
      background: {
        idle: [lightBlue[100], lightBlue[200]],
        hover: [lightBlue[200], lightBlue[400]],
        active: [lightBlue[300], lightBlue[500]],
      },
    },

    auxLightOrange: {
      background: {
        idle: [lightOrange[100], lightOrange[200]],
        hover: [lightOrange[200], lightOrange[400]],
        active: [lightOrange[300], lightOrange[500]],
      },
    },

    auxLightTeal: {
      background: {
        idle: [lightTeal[100], lightTeal[200]],
        hover: [lightTeal[200], lightTeal[300]],
        active: [lightTeal[300], lightTeal[400]],
      },
    },

    auxLightYellow: {
      background: {
        idle: [lightYellow[100], lightYellow[200]],
        hover: [lightYellow[200], lightYellow[300]],
        active: [lightYellow[300], lightYellow[400]],
      },
    },

    critical: {
      background: {
        idle: [red[50], red[100]],
        hover: [red[100], red[200]],
        active: [red[200], red[300]],
      },
    },

    success: {
      background: {
        idle: [green[50], green[100]],
        hover: [green[100], green[200]],
        active: [green[200], green[300]],
      },
    },

    disabled: {
      foreground: [smTokens.text.disabled, smTokens.text.disabled],
      background: [smTokens.surface.disabled, white[200]],
      border: [smTokens.border.disabled, 'transparent'],
    },
  },

  progress: {
    foreground: {
      default: [purple[500], purple[300]],
      critical: [red[700], red[500]],
      success: [green[700], green[500]],
    },
    background: [gray[400], gray[600]],
  },

  link: {
    idle: [smTokens.text.link, purple[200]],
    disabled: [smTokens.text.disabled, smTokens.text.disabled],
  },

  selectors: {
    border: {
      idle: [smTokens.border.secondary, smTokens.border.tertiary],
      on: [smTokens.border.brand, purple[400]],
    },
    background: {
      disabled: [black[50], white[100]],

      idle: [gray[500], gray[600]],
      hover: [smTokens.surface.tertiary.default, white[200]],
      filled: [smTokens.bg.brand.default, purple[400]],
    },

    foreground: {
      disabled: [smTokens.text.disabled, smTokens.text.disabled],
      primary: [smTokens.text.brand, purple[400]],
      filled: [smTokens.text.inverse, smTokens.text.inverse],
    },

    disabled: {
      foreground: [gray[400], white[300]],
      background: [gray[50], white[50]],
      border: [gray[400], white[300]],
    },
  },

  dropdownMenu: {
    background: {
      default: [smTokens.surface.primary.default, 'transparent'],
      hover: [smTokens.surface.primary.hover, white[200]],
      active: [smTokens.surface.primary.active, white[300]],
      disabled: [smTokens.surface.disabled, white[100]],
    },
    foreground: {
      default: [smTokens.text.primary, smTokens.text.inverse],
      disabled: [smTokens.text.disabled, smTokens.text.disabled],
    },
  },

  controls: {
    foreground: {
      label: [smTokens.text.secondary, smTokens.text.inverse],
      default: [smTokens.text.tertiary, gray[400]],
      input: [smTokens.text.primary, smTokens.text.inverse],
      success: [smTokens.text.alert.success, green[700]],
      critical: [smTokens.text.alert.critical, red[500]],
      disabled: [smTokens.text.disabled, smTokens.text.disabled],
    },
    background: {
      default: [smTokens.surface.primary.default, 'transparent'],
      hover: [smTokens.surface.primary.hover, white[200]],
      disabled: [smTokens.surface.disabled, white[100]],
    },
    border: {
      default: [smTokens.border.quaternary, gray[600]],
      hover: [smTokens.border.secondary, smTokens.border.tertiary],
      active: [smTokens.border.primary, smTokens.border.inverse],
      success: [smTokens.border.alert.success, green[700]],
      critical: [smTokens.border.alert.critical, red[500]],
    },
  },

  tooltip: {
    foreground: [smTokens.text.primary, smTokens.text.inverse],
    background: [smTokens.surface.secondary.default, gray[700]],

    prominent: {
      foreground: [smTokens.text.primary, smTokens.text.inverse],
      background: [purple[100], purple[600]],
    },
  },

  slider: {
    foreground: {
      default: [purple[600], purple[600]],
      disabled: [gray[500], gray[500]],
    },
    background: {
      default: [purple[100], purple[200]],
      disabled: [gray[300], gray[700]],
    },
  },
});

export const [componentLightTokens, componentDarkTokens] = componentTokens;
export const componentVars = createThemeContract(componentLightTokens);

export type ThemeColors =
  | typeof componentLightTokens
  | typeof componentDarkTokens;
